import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Download, Loader2, PenLine, Plus, Send, Trash2, FolderPlus, Receipt, BellRing, Star } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { PhotoUploader } from "@/components/PhotoUploader";
import { DateField } from "@/components/DateField";
import { studio, apiError, beDate, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { SignaturePad } from "@/components/studio/SignaturePad";
import { AiReportCard } from "@/components/studio/AiReportCard";
import { DiagnosticCard } from "@/components/studio/DiagnosticCard";
import { PageSkeleton } from "@/components/studio/StudioUI";

const fieldCls = "rounded-xl border border-[#E2E7F7] bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-[#93A0BC] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]/30 focus:border-[#3A5CC8]";

const Card = ({ title, icon: Icon, children, testId, action }) => (
  <section data-testid={testId} className="rounded-2xl border border-[#E2E7F7] bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#3A5CC8]" />} {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

export default function StudioIntervention() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [iv, setIv] = useState(null);
  const [busy, setBusy] = useState("");
  const [signName, setSignName] = useState("");
  const [signEmail, setSignEmail] = useState("");
  const [aiVersion, setAiVersion] = useState(0);
  const [isOwner, setIsOwner] = useState(false);
  useEffect(() => { studio.get("/whoami").then(({ data }) => setIsOwner(!!data.is_owner)).catch(() => {}); }, []);
  const pad = useRef(null);

  const load = useCallback(() => {
    studio.get(`/interventions/${id}`).then(({ data }) => setIv(data)).catch(() => toast.error("Chargement impossible."));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    try {
      const { data } = await studio.put(`/interventions/${id}`, updates);
      setIv(data);
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible."));
    }
  };

  const setWorks = (works) => patch({ works });

  const addWork = () => setWorks([...(iv.works || []), { id: crypto.randomUUID(), label: "", quantity: "", unit: "", done: true, note: "" }]);

  const sign = async () => {
    if (!signName.trim()) {
      toast.error("Indiquez le nom du client.");
      return;
    }
    const signature_data = pad.current?.toDataURL() || "";
    if (!signature_data) {
      toast.error("Faites signer le client dans le cadre prévu.");
      return;
    }
    setBusy("sign");
    try {
      await studio.put(`/interventions/${id}/sign`, { name: signName, email: signEmail, signature_data });
      toast.success("Rapport signé par le client.");
      setSignName("");
      setSignEmail("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Signature impossible."));
    } finally {
      setBusy("");
    }
  };

  const sendForSignature = async () => {
    setBusy("sign-link");
    try {
      await studio.post(`/interventions/${id}/send-signature`);
      toast.success("Lien de signature envoyé au client.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Envoi impossible."));
    } finally {
      setBusy("");
    }
  };

  const remindSignature = async () => {
    setBusy("remind");
    try {
      const { data } = await studio.post(`/interventions/${id}/remind-signature`);
      toast.success(`Relance envoyée à ${data.client_email}.`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Relance impossible."));
    } finally {
      setBusy("");
    }
  };

  const createDossier = async () => {
    setBusy("dossier");
    try {
      const { data } = await studio.post(`/interventions/${id}/dossier`);
      toast.success(`Dossier ${data.reference} créé depuis l'intervention.`);
      navigate(`/studio/dossiers/${data.id}`);
    } catch (e) {
      toast.error(apiError(e, "Création du dossier impossible."));
    } finally {
      setBusy("");
    }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await studio.get(`/interventions/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${iv.reference}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e, "Génération impossible."));
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    setBusy("send");
    try {
      await studio.post(`/interventions/${id}/send`);
      toast.success("Rapport envoyé au client.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Envoi impossible."));
    } finally {
      setBusy("");
    }
  };

  if (!iv) {
    return <StudioLayout title="Intervention"><PageSkeleton /></StudioLayout>;
  }

  return (
    <StudioLayout
      title={`Intervention ${iv.reference}`}
      subtitle={`${iv.client_name}${iv.site_city ? ` · ${iv.site_city}` : ""}${iv.quote_number ? ` · Devis ${iv.quote_number}` : ""}`}
    >
      <div className="mt-6 space-y-4">
        {iv.dossier_id ? (
          <Link
            to={`/studio/dossiers/${iv.dossier_id}`}
            data-testid="iv-dossier-link"
            className="flex items-center justify-between gap-3 rounded-2xl border border-[#E2E7F7] bg-white px-5 py-4 text-sm font-semibold text-[#0F2645] shadow-[0_1px_2px_rgba(15,38,69,0.04)] hover:border-[#3A5CC8] transition-colors duration-200"
          >
            Dossier lié : {iv.dossier_reference || "voir le dossier"}
            <FolderPlus className="w-4 h-4 text-[#3A5CC8]" />
          </Link>
        ) : (
          <Card testId="iv-create-dossier" title="Besoin d'un devis après cette intervention ?" icon={FolderPlus}>
            <p className="mt-1 text-sm text-[#5C6A87] leading-relaxed">
              Ouvrez un dossier chantier qui reprend automatiquement le client, l'adresse, les travaux réalisés, le matériel, les mesures et vos photos.
            </p>
            <button type="button" data-testid="iv-create-dossier-btn" onClick={createDossier} disabled={busy === "dossier"} className={`${btnPrimary} mt-4`}>
              {busy === "dossier" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />} Créer le dossier depuis cette intervention
            </button>
          </Card>
        )}

        <Card testId="iv-invoice" title="Facturer cette intervention" icon={Receipt}>
          <p className="mt-1 text-sm text-[#5C6A87] leading-relaxed">
            La facture reprend les travaux réalisés et le matériel utilisé, avec vos prix de la bibliothèque.
          </p>
          <button
            type="button"
            data-testid="iv-invoice-btn"
            disabled={busy === "invoice"}
            onClick={async () => {
            setBusy("invoice");
            try {
              const { data } = await studio.post(`/interventions/${id}/invoice`);
              toast.success(`Facture ${data.reference} créée.`);
              navigate(`/studio/factures/${data.id}`);
            } catch (e) { toast.error(apiError(e, "Facturation impossible.")); }
            finally { setBusy(""); }
            }}
            className={`${btnPrimary} mt-4`}
          >
            {busy === "invoice" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Créer la facture
          </button>
        </Card>

        <Card testId="iv-header" title="Sur place">
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2">
              <DateField testId="iv-date" placeholder="Date" value={iv.date} onChange={(v) => patch({ date: v })} />
            </div>
            <input data-testid="iv-arrival" type="time" className={inputCls} value={iv.arrival_time || ""} onChange={(e) => patch({ arrival_time: e.target.value })} />
            <input data-testid="iv-departure" type="time" className={inputCls} value={iv.departure_time || ""} onChange={(e) => patch({ departure_time: e.target.value })} />
          </div>
          <input
            data-testid="iv-technician"
            className={`${inputCls} mt-2`}
            placeholder="Technicien"
            defaultValue={iv.technician_name || ""}
            onBlur={(e) => e.target.value !== iv.technician_name && patch({ technician_name: e.target.value })}
          />
        </Card>

        <DiagnosticCard
          interventionId={id}
          diagnostic={iv.diagnostic || null}
          photosCount={(iv.photos || []).length}
          isOwner={isOwner}
          signed={!!iv.signature}
          onUpdated={(data) => { setIv((prev) => (data.id ? data : { ...prev, ...data })); if (data.id) setAiVersion((v) => v + 1); }}
        />

        {!iv.signature && <AiReportCard interventionId={id} photosCount={(iv.photos || []).length} onApplied={(data) => { setIv(data); setAiVersion((v) => v + 1); }} />}

        <Card
          testId="iv-works"
          title="Travaux réalisés"
          icon={CheckCircle2}
          action={<button type="button" data-testid="iv-add-work" onClick={addWork} className={btnGhost}><Plus className="w-3.5 h-3.5" /> Ligne</button>}
        >
          <p className="mt-1 text-xs text-[#5C6A87]">Repris du devis. Décochez ce qui n'a pas été fait, ajoutez ce qui s'est ajouté sur place.</p>
          <div className="mt-4 space-y-2">
            {(iv.works || []).map((w, i) => (
              <div key={w.id} data-testid={`iv-work-${i}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-[#E2E7F7] px-3 py-2">
                <button
                  type="button"
                  data-testid={`iv-work-toggle-${i}`}
                  onClick={() => setWorks(iv.works.map((x) => (x.id === w.id ? { ...x, done: !x.done } : x)))}
                  aria-label="Fait / pas fait"
                >
                  <CheckCircle2 className={`w-5 h-5 ${w.done ? "text-green-600" : "text-slate-300"}`} />
                </button>
                <input
                  data-testid={`iv-work-label-${i}`}
                  className={`${fieldCls} flex-1 min-w-[10rem]`}
                  defaultValue={w.label}
                  placeholder="Travail réalisé"
                  onBlur={(e) => e.target.value !== w.label && setWorks(iv.works.map((x) => (x.id === w.id ? { ...x, label: e.target.value } : x)))}
                />
                <input
                  data-testid={`iv-work-qty-${i}`}
                  className={`${fieldCls} w-20 flex-none`}
                  defaultValue={w.quantity}
                  placeholder="Qté"
                  onBlur={(e) => e.target.value !== w.quantity && setWorks(iv.works.map((x) => (x.id === w.id ? { ...x, quantity: e.target.value } : x)))}
                />
                <button
                  type="button"
                  data-testid={`iv-work-remove-${i}`}
                  onClick={() => setWorks(iv.works.filter((x) => x.id !== w.id))}
                  className="text-slate-300 hover:text-red-600 transition-colors"
                  aria-label="Retirer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {!iv.works?.length && <p className="text-sm text-[#5C6A87]">Aucun travail listé. Ajoutez une ligne.</p>}
          </div>
        </Card>

        <Card testId="iv-notes" title="Observations et recommandations">
          <textarea
            key={`obs-${aiVersion}`}
            data-testid="iv-observations"
            rows={3}
            className={`${inputCls} mt-3`}
            placeholder="Ce qui a été constaté et réalisé…"
            defaultValue={iv.observations || ""}
            onBlur={(e) => e.target.value !== iv.observations && patch({ observations: e.target.value })}
          />
          <textarea
            key={`rec-${aiVersion}`}
            data-testid="iv-recommendations"
            rows={2}
            className={`${inputCls} mt-2`}
            placeholder="Recommandations pour le client (entretien, points à surveiller…)"
            defaultValue={iv.recommendations || ""}
            onBlur={(e) => e.target.value !== iv.recommendations && patch({ recommendations: e.target.value })}
          />
        </Card>

        <Card testId="iv-photos" title="Photos de l'intervention">
          <PhotoUploader
            testId="iv-photo-uploader"
            hint="Avant / après, plaque, raccordements : jusqu'à 6 photos dans le rapport."
            photos={(iv.photos || []).map((p) => p.media_id)}
            onChange={async (nextIds) => {
              const current = (iv.photos || []).map((p) => p.media_id);
              const added = nextIds.filter((x) => !current.includes(x));
              const removed = (iv.photos || []).filter((p) => !nextIds.includes(p.media_id));
              try {
                for (const mediaId of added) {
                  await studio.post(`/interventions/${id}/photos`, { media_id: mediaId, category: "apres", caption: "" });
                }
                for (const photo of removed) {
                  await studio.delete(`/interventions/${id}/photos/${photo.id}`);
                }
                load();
              } catch (e) {
                toast.error(apiError(e, "Photo non enregistrée."));
              }
            }}
          />
        </Card>

        <Card testId="iv-signature" title="Signature du client" icon={PenLine}>
          {iv.signature ? (
            <>
              <p data-testid="iv-signed" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-1.5 text-xs font-semibold text-green-700">
                Signé par {iv.signature.name} le {beDate(iv.signature.signed_at)}
              </p>
              {iv.review?.rating ? (
                <div data-testid="iv-review" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="flex items-center gap-1 text-sm font-semibold text-[#0F2645]">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${iv.review.rating >= n ? "fill-amber-400 text-amber-400" : "text-amber-200"}`} />)}
                    <span className="ml-1">Avis client {iv.review.rating}/5</span>
                  </p>
                  {iv.review.comment && <p className="mt-1.5 text-sm text-[#0F2645]">« {iv.review.comment} »</p>}
                  <p className="mt-1 text-[11px] text-[#93A0BC]">Reçu le {beDate((iv.review.at || "").slice(0, 10))}</p>
                </div>
              ) : iv.review_requested_at ? (
                <p data-testid="iv-review-requested" className="mt-3 text-[11px] text-[#93A0BC]">Demande d'avis envoyée au client le {beDate(iv.review_requested_at.slice(0, 10))} · en attente de réponse</p>
              ) : null}
              <p data-testid="iv-sign-trace" className="mt-2 text-[11px] leading-relaxed text-[#93A0BC]">
                {iv.signature.method === "lien-email" ? "Signé à distance via le lien sécurisé" : "Signé sur place"}
                {iv.signature.email ? ` · ${iv.signature.email}` : ""}
                {iv.signature.ip ? ` · IP ${iv.signature.ip}` : ""} · horodatage conservé comme preuve (eIDAS)
              </p>
            </>
          ) : (
            <>
              <p className="mt-1 text-xs text-[#5C6A87]">
                Le client signe sur votre téléphone à la fin de l'intervention, ou à distance via un lien sécurisé.
              </p>
              <SignaturePad ref={pad} testId="iv-signature" className="mt-3 max-w-lg" />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input data-testid="iv-signature-name" className={`${fieldCls} w-56`} placeholder="Nom du client" value={signName} onChange={(e) => setSignName(e.target.value)} />
                <input data-testid="iv-signature-email" type="email" className={`${fieldCls} w-60`} placeholder="E-mail du signataire (facultatif)" value={signEmail} onChange={(e) => setSignEmail(e.target.value)} />
                <button type="button" data-testid="iv-signature-save" disabled={!!busy} onClick={sign} className={btnPrimary}>
                  {busy === "sign" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Valider la signature
                </button>
              </div>
              <div className="mt-4 border-t border-[#E2E7F7] pt-4">
                <p className="text-xs text-[#5C6A87]">Le client n'est plus sur place ? Envoyez-lui le rapport à signer.</p>
                <button type="button" data-testid="iv-send-signature" disabled={!!busy} onClick={sendForSignature} className={`${btnGhost} mt-2`}>
                  {busy === "sign-link" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Envoyer le lien de signature
                </button>
                {iv.signature_sent_at && (
                  <span data-testid="iv-signature-sent" className="ml-2 text-xs font-semibold text-[#3A5CC8]">
                    Envoyé le {beDate(iv.signature_sent_at)}
                  </span>
                )}
                {iv.signature_sent_at && (
                  <div data-testid="iv-signature-reminders" className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#5C6A87]">
                    <button type="button" data-testid="iv-remind-signature" disabled={!!busy} onClick={remindSignature} className={btnGhost}>
                      {busy === "remind" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Relancer maintenant
                    </button>
                    <span>
                      {iv.signature_reminders_sent
                        ? `${iv.signature_reminders_sent} relance(s) · dernière le ${beDate(iv.signature_reminder_at)}`
                        : "Relance automatique 3 puis 7 jours après l'envoi si le client n'a pas signé."}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>

        <Card testId="iv-actions" title="Rapport client">
          <p className="mt-1 text-xs text-[#5C6A87]">
            Rapport technique sans montants : travaux réalisés, matériel, mesures, photos et signature.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" data-testid="iv-pdf" disabled={!!busy} onClick={downloadPdf} className={btnGhost}>
              {busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Télécharger le PDF
            </button>
            <button type="button" data-testid="iv-send" disabled={!!busy} onClick={send} className={btnPrimary}>
              {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer au client
            </button>
            {iv.sent_at && <span data-testid="iv-sent-at" className="text-xs font-semibold text-green-700">Envoyé le {beDate(iv.sent_at)}</span>}
          </div>
        </Card>
      </div>
    </StudioLayout>
  );
}
